// src/components/reporting/RealAssessmentPage.js
// REAL Assessment tool — SuperAdmin only.
// Home view: two regions — existing reports list + run-new-assessment panel.
// Clicking an existing report fetches and renders it inline.
// Running a new report: Step 1 pick user → Step 2 pick conversation → poll → render.

import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import './Analytics.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};


// ---------------------------------------------------------------------------
// ConversationReport — new-schema report renderer
// ---------------------------------------------------------------------------

function stageRatingColor(rating) {
  if (rating === 'H') return { bg: 'var(--color-background-success)', text: 'var(--color-text-success)' };
  if (rating === 'M') return { bg: 'var(--color-background-warning)', text: 'var(--color-text-warning)' };
  return { bg: 'var(--color-background-danger)', text: 'var(--color-text-danger)' };
}

function scoreColor(score) {
  if (score >= 4) return 'var(--color-text-success)';
  if (score >= 3) return 'var(--color-text-warning)';
  return 'var(--color-text-danger)';
}

function scoreColorInverted(score) {
  if (score <= 2) return 'var(--color-text-success)';
  if (score <= 3) return 'var(--color-text-warning)';
  return 'var(--color-text-danger)';
}

function SectionLabel({ children }) {
  return (
    <p style={{
      fontSize: 11, fontWeight: 500, letterSpacing: '0.07em',
      textTransform: 'uppercase', color: 'var(--color-text-secondary)',
      margin: '0 0 10px',
    }}>
      {children}
    </p>
  );
}

function ReportCard({ children, style }) {
  return (
    <div style={{
      background: 'var(--color-background-primary)',
      border: '0.5px solid var(--color-border-tertiary)',
      borderRadius: 'var(--border-radius-lg)',
      padding: '1rem 1.25rem',
      ...style,
    }}>
      {children}
    </div>
  );
}

function ReportBadge({ label, bg, fg }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 11, fontWeight: 500, padding: '2px 8px',
      borderRadius: 100, background: bg, color: fg, flexShrink: 0,
    }}>
      {label}
    </span>
  );
}

function ScoreRow({ label, score, inverted = false }) {
  const color = inverted ? scoreColorInverted(score) : scoreColor(score);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12.5 }}>
      <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          background: color, display: 'inline-block',
        }} />
        <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{score}</span>
      </div>
    </div>
  );
}

function ReportDivider() {
  return <hr style={{ border: 'none', borderTop: '0.5px solid var(--color-border-tertiary)', margin: 0 }} />;
}

function Prose({ text, style }) {
  const paragraphs = (text || '').split('\n\n').filter(Boolean);
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} style={{
          fontSize: 13.5, lineHeight: 1.75,
          color: 'var(--color-text-secondary)',
          margin: i < paragraphs.length - 1 ? '0 0 0.75rem' : 0,
          ...style,
        }}>
          {p}
        </p>
      ))}
    </>
  );
}

function StageGrid({ stageSummary }) {
  const stages = [
    { key: 'ready',  label: 'Ready'  },
    { key: 'engage', label: 'Engage' },
    { key: 'agree',  label: 'Agree'  },
    { key: 'lead',   label: 'Lead'   },
  ];
  return (
    <div>
      <SectionLabel>Stage performance</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
        {stages.map(({ key, label }) => {
          const stage = stageSummary?.[key];
          if (!stage) return null;
          const colors = stageRatingColor(stage.rating);
          const isLow = stage.rating === 'L';
          return (
            <ReportCard
              key={key}
              style={{ borderColor: isLow ? 'var(--color-border-danger)' : 'var(--color-border-tertiary)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)' }}>{label}</span>
                <ReportBadge label={stage.rating} bg={colors.bg} fg={colors.text} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.4 }}>
                {stage.finding}
              </p>
            </ReportCard>
          );
        })}
      </div>
    </div>
  );
}

function HighestLeverage({ text }) {
  return (
    <div style={{
      background: 'var(--color-background-danger)',
      border: '0.5px solid var(--color-border-danger)',
      borderRadius: 'var(--border-radius-lg)',
      padding: '1rem 1.25rem',
    }}>
      <p style={{
        fontSize: 10, fontWeight: 500, letterSpacing: '0.07em',
        textTransform: 'uppercase', color: 'var(--color-text-danger)', margin: '0 0 6px',
      }}>
        Highest leverage change
      </p>
      <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary)', margin: 0, lineHeight: 1.55 }}>
        {text}
      </p>
    </div>
  );
}

function WhatHappened({ text }) {
  return (
    <div>
      <SectionLabel>What happened</SectionLabel>
      <ReportCard style={{ border: '1px solid var(--color-border-secondary)' }}>
        <Prose text={text} />
      </ReportCard>
    </div>
  );
}

function Readiness({ text }) {
  return (
    <div>
      <SectionLabel>Readiness</SectionLabel>
      <Prose text={text} />
    </div>
  );
}

function Strengths({ text }) {
  return (
    <div>
      <SectionLabel>What you&apos;re doing well</SectionLabel>
      <ReportCard style={{
        background: 'var(--color-background-success)',
        borderColor: 'var(--color-border-success, var(--color-border-tertiary))',
      }}>
        <Prose text={text} />
      </ReportCard>
    </div>
  );
}

function PhaseCard({ phase }) {
  const { phase_title, exchange_range, narrative, key_moment, key_moment_note } = phase;
  const [start, end] = exchange_range;
  const rangeLabel = start === end ? `Exchange ${start}` : `Exchanges ${start}–${end}`;

  return (
    <ReportCard style={key_moment ? { borderColor: 'var(--color-border-warning)', borderWidth: 1 } : {}}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
            {phase_title}
          </span>
          {key_moment && (
            <ReportBadge
              label="Key moment"
              bg="var(--color-background-warning)"
              fg="var(--color-text-warning)"
            />
          )}
        </div>
        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', flexShrink: 0, paddingTop: 2 }}>
          {rangeLabel}
        </span>
      </div>
      <Prose text={narrative} />
      {key_moment && key_moment_note && (
        <div style={{
          marginTop: 12,
          padding: '8px 12px',
          background: 'var(--color-background-warning)',
          borderRadius: 'var(--border-radius-md)',
          borderLeft: '3px solid var(--color-text-warning)',
        }}>
          <p style={{ fontSize: 12.5, color: 'var(--color-text-primary)', margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>
            {key_moment_note}
          </p>
        </div>
      )}
    </ReportCard>
  );
}

function ConversationBreakdown({ phases }) {
  return (
    <div>
      <SectionLabel>The conversation, moment by moment</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {phases.map((phase) => (
          <PhaseCard key={phase.phase_title} phase={phase} />
        ))}
      </div>
    </div>
  );
}

const PRIORITY_COLORS = [
  { bg: 'var(--color-background-danger)',    fg: 'var(--color-text-danger)'    },
  { bg: 'var(--color-background-warning)',   fg: 'var(--color-text-warning)'   },
  { bg: 'var(--color-background-secondary)', fg: 'var(--color-text-secondary)' },
];

function Debrief({ items }) {
  const sorted = [...items].sort((a, b) => a.priority - b.priority);
  return (
    <div>
      <SectionLabel>Your debrief</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map((item) => {
          const colors = PRIORITY_COLORS[item.priority - 1] || PRIORITY_COLORS[2];
          return (
            <ReportCard key={item.priority}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: colors.bg, color: colors.fg,
                  fontSize: 12, fontWeight: 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 1,
                }}>
                  {item.priority}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <p style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--color-text-primary)', margin: 0, lineHeight: 1.4 }}>
                      {item.change}
                    </p>
                    <ReportBadge
                      label={item.stage}
                      bg="var(--color-background-secondary)"
                      fg="var(--color-text-secondary)"
                    />
                  </div>
                  <div style={{
                    background: 'var(--color-background-secondary)',
                    borderRadius: 'var(--border-radius-md)',
                    padding: '10px 12px',
                  }}>
                    <p style={{
                      fontSize: 11, fontWeight: 500, letterSpacing: '0.05em',
                      textTransform: 'uppercase', color: 'var(--color-text-secondary)', margin: '0 0 4px',
                    }}>
                      Try this
                    </p>
                    <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                      {item.picture}
                    </p>
                  </div>
                </div>
              </div>
            </ReportCard>
          );
        })}
      </div>
    </div>
  );
}

function ScoresGrid({ scores }) {
  const sections = [
    scores.ready && {
      label: 'Ready',
      rows: [
        { label: 'Composite',          score: scores.ready.composite          },
        // Reports scored before the rename carry problem_framing.
        { label: 'Issue framing',      score: scores.ready.issue_framing ?? scores.ready.problem_framing },
        { label: 'Stakes modeling',    score: scores.ready.stakes_modeling    },
        { label: 'Evidence quality',   score: scores.ready.evidence_quality   },
        { label: 'Challenge accuracy', score: scores.ready.challenge_accuracy },
      ],
    },
    scores.engage && {
      label: 'Engage',
      rows: [
        { label: 'Composite',             score: scores.engage.composite             },
        { label: 'Frame quality',         score: scores.engage.frame_quality         },
        { label: 'Express effectiveness', score: scores.engage.express_effectiveness },
      ],
    },
    scores.agree && {
      label: 'Agree',
      rows: [
        { label: 'Composite',              score: scores.agree.composite              },
        { label: 'Explore depth',          score: scores.agree.explore_depth          },
        { label: 'Listen quality',         score: scores.agree.listen_quality         },
        { label: 'Agreement authenticity', score: scores.agree.agreement_authenticity },
      ],
    },
    (scores.lead || scores.behavioral) && {
      label: 'Lead & Behavioral',
      rows: [
        scores.lead && { label: 'Lead composite',     score: scores.lead.composite          },
        scores.lead && { label: 'Action specificity', score: scores.lead.action_specificity },
        scores.behavioral && { label: 'Say/think gap', score: scores.behavioral.say_think_gap_severity, inverted: true },
      ].filter(Boolean),
    },
  ].filter(Boolean);

  return (
    <div>
      <SectionLabel>Assessment scores</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        {sections.map((sec) => (
          <ReportCard key={sec.label}>
            <p style={{
              fontSize: 11, fontWeight: 500, letterSpacing: '0.05em',
              textTransform: 'uppercase', color: 'var(--color-text-secondary)', margin: '0 0 10px',
            }}>
              {sec.label}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {sec.rows.map((r) => (
                <ScoreRow key={r.label} label={r.label} score={r.score} inverted={r.inverted || false} />
              ))}
            </div>
          </ReportCard>
        ))}
      </div>
    </div>
  );
}

function ConversationReport({ data }) {
  const { report, metadata } = data;
  if (!report.what_happened) {
    return (
      <div style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-secondary)', padding: '2rem', textAlign: 'center' }}>
        <p style={{ margin: 0 }}>This report was generated with an earlier version and can no longer be displayed.</p>
      </div>
    );
  }
  const {
    highest_leverage_change,
    readiness,
    strengths,
    what_happened,
    conversation_breakdown,
    debrief,
    playbook,
  } = report;
  const debriefItems = debrief ?? playbook; // fallback for reports stored before the debrief rename
  const { stage_summary, all_scores } = metadata;

  return (
    <div style={{
      fontFamily: 'var(--font-sans)',
      color: 'var(--color-text-primary)',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
    }}>
      {stage_summary && <StageGrid stageSummary={stage_summary} />}
      {highest_leverage_change && <HighestLeverage text={highest_leverage_change} />}
      {what_happened && <WhatHappened text={what_happened} />}
      {(readiness || strengths) && <ReportDivider />}
      {readiness && <Readiness text={readiness} />}
      {strengths && <Strengths text={strengths} />}
      {conversation_breakdown?.length > 0 && (
        <>
          <ReportDivider />
          <ConversationBreakdown phases={conversation_breakdown} />
        </>
      )}
      {debriefItems?.length > 0 && (
        <>
          <ReportDivider />
          <Debrief items={debriefItems} />
        </>
      )}
      {/* remove the scores grid since we don't want the user to think this is a report card. 
      {all_scores && (
        <>
          <ReportDivider />
          <ScoresGrid scores={all_scores} />
        </>
      )} */}
    </div>
  );
}

SectionLabel.propTypes = { children: PropTypes.node };
ReportCard.propTypes = { children: PropTypes.node, style: PropTypes.object };
ReportBadge.propTypes = { label: PropTypes.string, bg: PropTypes.string, fg: PropTypes.string };
ScoreRow.propTypes = { label: PropTypes.string, score: PropTypes.number, inverted: PropTypes.bool };
ReportDivider.propTypes = {};
Prose.propTypes = { text: PropTypes.string, style: PropTypes.object };
StageGrid.propTypes = { stageSummary: PropTypes.object.isRequired };
HighestLeverage.propTypes = { text: PropTypes.string };
WhatHappened.propTypes = { text: PropTypes.string };
Readiness.propTypes = { text: PropTypes.string };
Strengths.propTypes = { text: PropTypes.string };
PhaseCard.propTypes = { phase: PropTypes.object.isRequired };
ConversationBreakdown.propTypes = { phases: PropTypes.array.isRequired };
Debrief.propTypes = { items: PropTypes.array.isRequired };
ScoresGrid.propTypes = { scores: PropTypes.object.isRequired };
ConversationReport.propTypes = { data: PropTypes.object.isRequired };

// ---------------------------------------------------------------------------
// Existing reports list
// ---------------------------------------------------------------------------

const ExistingReportsList = ({ tenant, user, onSelect }) => {
  const { loading, makeRequest } = useApiCall();
  const [reports, setReports] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setFetchError(null);
      try {
        const params = new URLSearchParams({ tenantId: tenant.id, limit: 50, sortBy: 'created_at', sortOrder: 'DESC' });
        const data = await makeRequest(
          () => apiService.get(`/api/v1/analytics/reports/assessments?${params}`, user),
          'Loading assessments…'
        );
        if (data.success) {
          setReports(data.data?.reports || []);
        }
      } catch (err) {
        setFetchError('Failed to load existing assessments');
      }
    };
    load();
  }, [tenant.id]);

  if (loading && reports.length === 0) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-4 justify-center">
        <div className="loading-dots"><div className="loading-dot"/><div className="loading-dot"/><div className="loading-dot"/></div>
        <span className="text-sm ml-2">Loading assessments…</span>
      </div>
    );
  }

  if (fetchError) {
    return <p className="text-red-600 text-sm py-3">{fetchError}</p>;
  }

  if (reports.length === 0) {
    return <p className="text-gray-500 text-sm text-center py-4">No assessments run yet for this tenant.</p>;
  }

  return (
    <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
      {reports.map(r => {
        const userName = r.subjectUser?.name || r.subjectUser?.email || '—';
        // Extract conversation title from report title: "REAL Assessment — {title}"
        const convTitle = r.title?.replace(/^REAL Assessment\s*[—-]\s*/i, '') || r.title || 'Untitled';
        return (
          <button
            key={r.id}
            onClick={() => onSelect(r.id)}
            className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center justify-between"
          >
            <div>
              <div className="font-medium text-gray-800">{convTitle}</div>
              <div className="text-xs text-gray-500 mt-0.5 flex gap-3">
                <span>{userName}</span>
                <span>{formatDate(r.createdAt)}</span>
                {r.status !== 'completed' && (
                  <span className="text-amber-600 capitalize">{r.status}</span>
                )}
              </div>
            </div>
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 1 — User picker
// ---------------------------------------------------------------------------

const UserPicker = ({ tenant, user, onSelect, onBack }) => {
  const { loading, makeRequest } = useApiCall();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const load = async (pg = 1, q = search) => {
    setFetchError(null);
    try {
      const params = new URLSearchParams({ page: pg, limit: 20, status: 'active' });
      if (q) params.set('search', q);
      const data = await makeRequest(
        () => apiService.get(`/api/v1/tenants/${tenant.id}/users?${params}`, user),
        pg === 1 ? 'Loading users...' : null
      );
      if (data.success) {
        const list = data.data?.users || data.data || [];
        setUsers(pg === 1 ? list : prev => [...prev, ...list]);
        setHasMore(data.data?.pagination?.hasMore || false);
        setPage(pg);
      }
    } catch (err) {
      setFetchError('Failed to load users');
    }
  };

  useEffect(() => { load(1, ''); }, [tenant.id]);

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearch(q);
    load(1, q);
  };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back
      </button>

      <h3 className="text-base font-semibold text-gray-800 mb-3">
        Select a user from <span className="text-blue-700">{tenant.name}</span>
      </h3>

      <input
        type="text"
        placeholder="Search by name or email…"
        value={search}
        onChange={handleSearch}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />

      {fetchError && <p className="text-red-600 text-sm mb-2">{fetchError}</p>}

      {loading && users.length === 0 ? (
        <div className="flex items-center gap-2 text-gray-500 py-6 justify-center">
          <div className="loading-dots"><div className="loading-dot"/><div className="loading-dot"/><div className="loading-dot"/></div>
          <span className="text-sm ml-2">Loading users…</span>
        </div>
      ) : users.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-6">No users found.</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {users.map(u => (
            <button
              key={u.id}
              onClick={() => onSelect(u)}
              className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center justify-between"
            >
              <div>
                <div className="font-medium text-gray-800">{u.displayName || u.display_name || u.email}</div>
                <div className="text-xs text-gray-500">{u.email}</div>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
        </div>
      )}

      {hasMore && (
        <button
          onClick={() => load(page + 1)}
          disabled={loading}
          className="mt-3 w-full btn btn-secondary btn-sm"
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 2 — Conversation picker
// ---------------------------------------------------------------------------

const ConversationPicker = ({ selectedUser, user, onSelect, onBack }) => {
  const { loading, makeRequest } = useApiCall();
  const [conversations, setConversations] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setFetchError(null);
      try {
        const data = await makeRequest(
          () => apiService.get(`/api/v1/reports/useractivity/${selectedUser.id}`, user),
          'Loading conversations…'
        );
        if (data.success && data.data?.conversations) {
          setConversations(data.data.conversations.filter(
            c => c.conversationStatus === 'active' && c.conversationMethodology === 'basic'
          ));
        } else {
          setConversations([]);
        }
      } catch (err) {
        setFetchError('Failed to load conversations for this user');
      }
    };
    load();
  }, [selectedUser.id]);

  const userName = selectedUser.displayName || selectedUser.display_name || selectedUser.email;

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to user list
      </button>

      <h3 className="text-base font-semibold text-gray-800 mb-3">
        Select a conversation for <span className="text-blue-700">{userName}</span>
      </h3>

      {fetchError && <p className="text-red-600 text-sm mb-2">{fetchError}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-6 justify-center">
          <div className="loading-dots"><div className="loading-dot"/><div className="loading-dot"/><div className="loading-dot"/></div>
          <span className="text-sm ml-2">Loading conversations…</span>
        </div>
      ) : conversations.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-6">No active basic-methodology conversations found for this user.</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {conversations.map(conv => (
            <button
              key={conv.conversationId}
              onClick={() => onSelect(conv)}
              className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center justify-between"
            >
              <div>
                <div className="font-medium text-gray-800">{conv.conversationTitle || 'Untitled Conversation'}</div>
                <div className="text-xs text-gray-500 mt-0.5 flex gap-3">
                  {conv.conversationPartner && <span>Counterpart: {conv.conversationPartner}</span>}
                  {conv.messageCount && <span>{conv.messageCount} messages</span>}
                  {conv.conversationCreatedAt && <span>{formatDate(conv.conversationCreatedAt)}</span>}
                </div>
              </div>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Running state (polls until complete)
// ---------------------------------------------------------------------------

const RunningState = ({ reportId, user, onDone, onError }) => {
  const intervalRef = useRef(null);

  useEffect(() => {
    const poll = async () => {
      try {
        const data = await apiService.get(`/api/v1/analytics/reports/assessments/${reportId}`, user);
        if (data.success && data.data) {
          const { status } = data.data;
          if (status === 'completed') {
            clearInterval(intervalRef.current);
            onDone(data.data);
          } else if (status === 'failed') {
            clearInterval(intervalRef.current);
            onError(data.data.error?.message || 'Assessment failed');
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, 3000);
    return () => clearInterval(intervalRef.current);
  }, [reportId]);

  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="loading-dots">
        <div className="loading-dot"/><div className="loading-dot"/><div className="loading-dot"/>
      </div>
      <p className="text-gray-700 font-medium">Running REAL Assessment…</p>
      <p className="text-gray-500 text-sm">This runs three analytical passes and takes about 60–90 seconds.</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Report viewer
// ---------------------------------------------------------------------------

const ReportViewer = ({ report, conversationTitle, userName, onBack, onDelete }) => {
  const result = report.result || {};

  return (
    <div>
      {/* Header bar */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          <h3 className="text-lg font-bold text-gray-900">{report.title}</h3>
          <div className="text-sm text-gray-500 mt-1 flex gap-3">
            {userName && <span>User: {userName}</span>}
            {conversationTitle && <span>· {conversationTitle}</span>}
            <span>· {formatDate(report.createdAt)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 real-assessment-actions">
          <button onClick={() => window.print()} className="btn btn-secondary btn-sm flex items-center gap-1" title="Save as PDF">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>Download PDF</span>
          </button>
          {onDelete && (
            <button onClick={onDelete} className="report-delete-btn" title="Delete report">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <ConversationReport data={result} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const RealAssessmentPage = ({ tenant, onBack }) => {
  const { user } = useAuth();
  const { makeRequest } = useApiCall();

  // step: 'home' | 'select-user' | 'select-conversation' | 'running' | 'done' | 'error' | 'view-existing'
  const [step, setStep] = useState('home');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [report, setReport] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // For viewing an existing report from the list
  const [existingReport, setExistingReport] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [existingError, setExistingError] = useState(null);

  const handleUserSelect = (u) => {
    setSelectedUser(u);
    setStep('select-conversation');
  };

  // `rerun` is sent only after the operator confirms re-assessing a conversation
  // that already has a live report — an unflagged duplicate is rejected with
  // 409 ASSESSMENT_EXISTS rather than silently creating a second one.
  const postAssessment = (conv, rerun) => makeRequest(
    () => apiService.post('/api/v1/analytics/reports/templates/real_assessment', {
      conversationId: conv.conversationId,
      title: `REAL Assessment — ${conv.conversationTitle || 'Conversation'}`,
      ...(rerun ? { rerun: true } : {})
    }, user),
    rerun ? 'Starting new assessment…' : 'Starting assessment…'
  );

  const handleConversationSelect = async (conv) => {
    setSelectedConversation(conv);
    setSubmitting(true);
    try {
      let data;
      try {
        data = await postAssessment(conv, false);
      } catch (err) {
        if (err?.code !== 'ASSESSMENT_EXISTS') throw err;
        const confirmed = window.confirm(
          'An assessment already exists for this conversation.\n\n' +
          'Run a new one? The existing report is archived, not deleted — it stays ' +
          'listed here, and the new report replaces it everywhere else it appears.'
        );
        // Declining leaves the picker up rather than dropping into the error step.
        if (!confirmed) return;
        data = await postAssessment(conv, true);
      }

      if (data.success && data.data?.reportId) {
        setReportId(data.data.reportId);
        setStep('running');
      } else {
        setErrorMsg('Failed to start assessment');
        setStep('error');
      }
    } catch (err) {
      setErrorMsg(err.message || 'Failed to start assessment');
      setStep('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReport = async (id) => {
    if (!window.confirm('Delete this assessment report?')) return;
    try {
      await makeRequest(
        () => apiService.delete(`/api/v1/analytics/reports/${id}`, user),
        'Deleting report…'
      );
      setStep('home');
      setReport(null);
      setExistingReport(null);
      setReportId(null);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleSelectExisting = async (id) => {
    setLoadingExisting(true);
    setExistingError(null);
    setExistingReport(null);
    setStep('view-existing');
    try {
      const data = await apiService.get(`/api/v1/analytics/reports/assessments/${id}`, user);
      if (data.success && data.data) {
        setExistingReport(data.data);
      } else {
        setExistingError('Failed to load report');
      }
    } catch (err) {
      setExistingError(err.message || 'Failed to load report');
    } finally {
      setLoadingExisting(false);
    }
  };

  const userName = selectedUser
    ? (selectedUser.displayName || selectedUser.display_name || selectedUser.email)
    : null;

  const isRunningNewReport = ['select-user', 'select-conversation', 'running', 'done', 'error'].includes(step);

  return (
    <div className="analytics-page">
      {/* Page header */}
      <div className="analytics-header mb-6">
        <div className="analytics-header-left">
          <button onClick={onBack} className="btn btn-secondary mr-3" title="Back to Reporting Dashboard">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h2 className="analytics-title">REAL Assessment</h2>
            <p className="text-sm text-gray-600 mt-1">
              {tenant.name}
              {selectedUser && isRunningNewReport && <> · {userName}</>}
              {selectedConversation && isRunningNewReport && <> · {selectedConversation.conversationTitle || 'Conversation'}</>}
            </p>
          </div>
        </div>
      </div>

      <div className="reports-container">

        {/* ── Home: two-region layout ── */}
        {step === 'home' && (
          <div className="space-y-8">
            {/* Region 1: Run new assessment */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-5 py-4 flex items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-1">Run New Assessment</h3>
                <p className="text-sm text-gray-500">
                  Select a user and conversation to run a three-pass REAL assessment (60–90 seconds).
                </p>
              </div>
              <button
                onClick={() => setStep('select-user')}
                className="btn btn-primary flex-shrink-0"
              >
                Start New Assessment
              </button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="border-t border-gray-200" />
              <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-white px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Past Assessments
              </span>
            </div>

            {/* Region 2: Existing reports */}
            <div>
              <ExistingReportsList tenant={tenant} user={user} onSelect={handleSelectExisting} />
            </div>
          </div>
        )}

        {/* ── View an existing report ── */}
        {step === 'view-existing' && (
          <>
            {loadingExisting && (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="loading-dots">
                  <div className="loading-dot"/><div className="loading-dot"/><div className="loading-dot"/>
                </div>
                <p className="text-gray-500 text-sm">Loading report…</p>
              </div>
            )}
            {existingError && (
              <div className="text-center py-12">
                <div className="text-red-600 font-medium mb-2">Failed to load report</div>
                <p className="text-gray-500 text-sm mb-4">{existingError}</p>
                <button onClick={() => setStep('home')} className="btn btn-secondary">Back</button>
              </div>
            )}
            {!loadingExisting && existingReport && (
              <ReportViewer
                report={existingReport}
                conversationTitle={existingReport.title?.replace(/^REAL Assessment\s*[—-]\s*/i, '') || null}
                userName={existingReport.subjectUser?.name || existingReport.subjectUser?.email || null}
                onBack={() => { setStep('home'); setExistingReport(null); }}
                onDelete={() => handleDeleteReport(existingReport.id)}
              />
            )}
          </>
        )}

        {/* ── New report flow ── */}
        {step === 'select-user' && (
          <UserPicker
            tenant={tenant}
            user={user}
            onSelect={handleUserSelect}
            onBack={() => setStep('home')}
          />
        )}

        {step === 'select-conversation' && selectedUser && (
          <>
            <ConversationPicker
              selectedUser={selectedUser}
              user={user}
              onSelect={handleConversationSelect}
              onBack={() => setStep('select-user')}
            />
            {submitting && (
              <div className="text-center py-4 text-gray-500 text-sm">Starting assessment…</div>
            )}
          </>
        )}

        {step === 'running' && reportId && (
          <RunningState
            reportId={reportId}
            user={user}
            onDone={(r) => { setReport(r); setStep('done'); }}
            onError={(msg) => { setErrorMsg(msg); setStep('error'); }}
          />
        )}

        {step === 'done' && report && (
          <ReportViewer
            report={report}
            conversationTitle={selectedConversation?.conversationTitle}
            userName={userName}
            onBack={() => {
              setStep('home');
              setReport(null);
              setReportId(null);
              setSelectedUser(null);
              setSelectedConversation(null);
            }}
            onDelete={() => handleDeleteReport(reportId)}
          />
        )}

        {step === 'error' && (
          <div className="text-center py-12">
            <div className="text-red-600 font-medium mb-2">Assessment failed</div>
            <p className="text-gray-500 text-sm mb-4">{errorMsg}</p>
            <button
              onClick={() => { setStep('select-conversation'); setErrorMsg(null); }}
              className="btn btn-secondary"
            >
              Try a different conversation
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

RealAssessmentPage.propTypes = {
  tenant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  onBack: PropTypes.func.isRequired
};


ExistingReportsList.propTypes = {
  tenant: PropTypes.object.isRequired,
  user: PropTypes.object,
  onSelect: PropTypes.func.isRequired
};

UserPicker.propTypes = {
  tenant: PropTypes.object.isRequired,
  user: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

ConversationPicker.propTypes = {
  selectedUser: PropTypes.object.isRequired,
  user: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

RunningState.propTypes = {
  reportId: PropTypes.string.isRequired,
  user: PropTypes.object,
  onDone: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
};

ReportViewer.propTypes = {
  report: PropTypes.object.isRequired,
  conversationTitle: PropTypes.string,
  userName: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onDelete: PropTypes.func
};

export default RealAssessmentPage;
